/**
 * 案件アサイン（要件定義 / 開発）で FE / BE が共有する契約。
 *
 * 画面側（送信前バリデーション）と API 側（zod スキーマの上限）が同じ値を参照できるよう、
 * ワークスペース個別に字面定数を重複定義せず、ここを単一の SoT にする
 * （`docs/guidelines/architecture.md`「共有型・実装のワークスペース個別定義」反パターンの回避）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件アサインの FE/BE 共有定数（1 リクエストの最大行数・作らないと決められる成果物・AI 仮判断の状態・宣言と仮判断の関係）
 * @graph-connects none
 */
/**
 * 1 リクエストで受け付ける最大行数。
 *
 * 1 行あたり Backlog へ GET 2 回 + PATCH 1〜3 回 + Slack 2 回が走るため、貼り間違いで数百行
 * 送られると Backlog のレート制限に当たって途中から全部失敗する。API 側が本体の制限
 * （画面を経由しない呼び出しもある）で、画面側は送信前に同じ上限で気づかせる。
 * @graph-connects none
 */
export const MAX_ASSIGN_ROWS = 50;
/**
 * 要件定義アサインのときに「作らない」と決められる成果物。
 *
 * 判定の正本は `@cortex/domain-spec-flow` の `SKIPPABLE_ARTIFACTS`。あちらは Firestore SDK と
 * `node:fs` を読むので画面からは import できないため、FE/BE が共有できるここへ写している
 * （`apps/api/product` のテストが両者の一致を機械で守る。片方だけ足されると、画面が出した
 * 成果物を API が弾くというずれ方をする）。
 * @graph-connects none
 */
export const ASSIGN_SKIPPABLE_ARTIFACTS = [
    'business_flow',
    'features',
    'validation_list',
    'integration_test',
    'narrative',
];
/** 成果物の表示名（画面のチェックボックスと Slack 通知で同じ名前を使う）。 @graph-connects none */
export const ASSIGN_SKIPPABLE_ARTIFACT_LABELS = {
    business_flow: '業務フロー',
    features: '機能一覧',
    validation_list: 'バリデーション一覧',
    integration_test: '総合テスト',
    narrative: 'ナラティブ',
};
/** 作らない理由の文字数の上限。 @graph-connects none */
export const ASSIGN_SKIP_REASON_MAX = 200;
/**
 * 「作らない成果物」の AI 仮判断の結果の種類。
 *
 * **仮判断が無いことと「全部作る」を区別する**ための状態。区別しないと、憲章が無い案件や
 * AI が落ちた案件が「作らないものは無いと判断された」ように見える。
 * @graph-connects none
 */
export const ASSIGN_SCOPE_SUGGESTION_STATUSES = [
    /** 仮判断ができた（`skipped` が初期値。空配列なら「全部作る」と判断した）。 */
    'suggested',
    /** プロジェクト憲章が無く、判断材料が無かった。 */
    'noCharter',
    /** プロジェクト憲章を読めなかった（案件仕様書が共有されていない等）。 */
    'charterUnreadable',
    /** AI の判定が失敗した。 */
    'judgeFailed',
];
/**
 * 宣言が AI の仮判断とどういう関係にあったか（判定の正本は `@cortex/domain-spec-flow` の
 * `SCOPE_DECISION_ORIGINS`）。
 *
 * **「そのまま通したか」を結果表に出すための写し。** 記録されたことを実行した人がその場で
 * 確かめられるようにする（画面と API のデプロイがずれた窓では返らないので、それが
 * 「まだ記録されていない」の合図になる）。
 * @graph-connects none
 */
export const ASSIGN_SCOPE_DECISION_ORIGINS = [
    'ai_accepted',
    'ai_reworded',
    'ai_edited',
    'ai_unavailable',
    'no_suggestion',
];
/** 宣言と仮判断の関係の表示名。 @graph-connects none */
export const ASSIGN_SCOPE_DECISION_ORIGIN_LABELS = {
    ai_accepted: 'AI の仮判断どおり',
    ai_reworded: 'AI の仮判断どおり（理由は書き直し）',
    ai_edited: 'AI の仮判断を変更',
    ai_unavailable: 'AI は仮判断できず（人が判断）',
    no_suggestion: '仮判断を経ていない宣言',
};
//# sourceMappingURL=project-assign.js.map